import { useEffect, useState } from "react";
import { api, focusGraph } from "../api";
import { fmtTime } from "../format";
import { BOOK_LABEL, PricePath, fmtHold } from "./Performance";
import { px, signedUsd, usd } from "../lib/money";

const isFx = (s: string) => s.includes("_");
// FX trades in currency UNITS and crypto in coins — neither is "shares".
const unitWord = (s: string) => (isFx(s) ? "units" : s.includes("/") ? "coins" : "shares");

// A printed story is a RECORD — it gets filed, mailed, and searched for
// later, so its filename has to say what it is. Browsers take the PDF's
// default name from document.title, which was just "Stonky — a governed AI
// trading firm" for every single one (Alan, 2026-08-04: "the filename should
// follow the situation. ie asset date, action"). Now: what, which way, when,
// what closed it, and what it cost.
function storyFilename(id: {
  symbol: string; side: string; book: string; pnl: number;
  exit_time: string | null; entry_time: string | null;
  result?: string; exit_reason?: string | null;
}): string {
  const clean = (v: string) =>
    v.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  // "/" and "_" are legal in symbols but not in filenames — BTC/USD would
  // otherwise read as a directory.
  const sym = id.symbol.replace(/[/_]/g, "-").toUpperCase();
  const when = (id.exit_time || id.entry_time || "").slice(0, 10) || "undated";
  const action = id.exit_reason ? clean(id.exit_reason) : (id.result ? clean(id.result) : "closed");
  const mag = Math.abs(id.pnl);
  const pnl = `${id.pnl < 0 ? "-" : "+"}${mag < 1 ? mag.toFixed(4) : mag.toFixed(2)}`;
  return ["stonky", sym, clean(id.side), when, action, pnl, clean(id.book)]
    .filter(Boolean).join("_");
}

type Directive = {
  id?: string; seq: number; text?: string; status?: string; trading_day?: string;
  stale?: boolean; found: boolean;
};
type Proposal = {
  id: string; junior: string; symbol: string; side: string; qty: number;
  entry_type: string; limit_price: number | null; exit_target: number | null;
  exit_stop: number | null; exit_time: string | null; rationale: string; status: string;
  peer_feedback: Record<string, unknown> | null; risk_report: { allowed?: boolean; checks?: { name: string; ok: boolean; detail: string }[] } | null;
  trading_day: string;
};
type Perspective = { model?: string; text?: string };
type BoardSession = {
  id: string; kind: string; status: string; started_at: string | null; completed_at: string | null;
  summary: string | null; perspectives: Record<string, Perspective>;
  devils_advocate?: { statement?: string } | null;
  risk_officer?: { verdict?: string; notes?: string[] } | null;
  stonky?: { verdict?: string; vetoed?: (number | string)[]; statement?: string } | null;
};
type SeniorRuling = { decision: string; reasoning: string; created_at: string };
type Mechanical = {
  rule: string; strike_price?: number | null; trigger_price?: number | null;
  target?: number | null; invalidation_stop?: number | null; rationale?: string;
  budget_check?: Record<string, number | string>;
};
type ExecSummary = {
  headline: string; points: string[]; flags: string[];
  // money: real vs. paper reshapes what the summary MEANS, not just a label
  // (Alan, 2026-08-06) — purpose_note states the stakes plainly, root_cause
  // is only ever populated for a REAL-MONEY LOSS: the single leading factor
  // worth fixing, synthesized from the flags above.
  money: "real" | "paper"; purpose_note: string; root_cause: string | null;
};
type TradeBrief = {
  trade_key: string | null; symbol: string; side: string; qty: number;
  pnl: number; book: string; money?: "real" | "paper"; exit_time: string | null; junior: string | null;
};
type Agg = { trades: number; net_pnl: number; wins: number; losses: number };
type Branching = {
  fan_in_same_exit: { count: number; siblings: TradeBrief[]; combined?: Agg };
  split_exits_same_entry: { count: number; siblings: TradeBrief[] };
  directive_fanout: (Agg & { seq: number; peers: TradeBrief[] })[];
  session_fanout: (Agg & { directives: number[]; this_trade_pnl: number; trades_list: TradeBrief[] }) | null;
};
export type Story = {
  found: true;
  summary: ExecSummary | null;
  branching: Branching;
  identity: {
    symbol: string; side: string; qty: number; book: string; money: "real" | "paper"; junior: string | null;
    entry_price: number; exit_price: number; entry_time: string | null; exit_time: string | null;
    pnl: number; pnl_pct: number; result?: string; hold_min: number | null;
    exit_reason?: string | null;
  };
  backward: { proposal: Proposal | null; directives: Directive[]; board_session: BoardSession | null;
             senior_ruling: SeniorRuling | null; mechanical: Mechanical | null };
  forward: {
    market_forensics: { path: { t: string; c: number }[];
                        if_held: { label: string; price: number; would_pnl: number; vs_actual: number; final?: boolean }[];
                        best_exit: { price: number; t: string; would_pnl: number; left_on_table: number } | null;
                        signal_freshness: Record<string, { at: string; min_before_entry: number }> };
    post_mortem: { trading_day: string; content: string; mentions_symbol: boolean } | null;
    trend_insights: { kind: string; content: string; created_at: string }[];
  };
  graph_ids: string[];
  network?: { nodes: { id: string; type: string; label: string;
                       // hover: server-computed "what this step provided /
                       // influenced by / set up"; side: buy|sell on Orders
                       // so entry and exit stop rendering identically.
                       hover?: string; side?: string }[];
              edges: { src: string; rel: string; dst: string }[];
              by_type: Record<string, number> };
};

type OpenPosition = { symbol: string; side: string; qty: number; entry_px: number; entry_time: string; book: string };
type StoryResponse = Story | { found: false; open_position?: OpenPosition };

export default function TradeStory({ exitOrderId, onClose }: { exitOrderId: string; onClose: () => void }) {
  const [story, setStory] = useState<Story | null>(null);
  const [openPosition, setOpenPosition] = useState<OpenPosition | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.get<StoryResponse>(`/api/performance/trade/story?exit_order_id=${exitOrderId}`)
      .then((d) => {
        if (!live) return;
        if (d.found) { setStory(d); return; }
        // The most common "not found" isn't broken — it's a still-open
        // position (crypto/forex sleeves hold for days), most often hit by
        // clicking an Order node in Graph Explorer before it's been exited.
        if (d.open_position) { setOpenPosition(d.open_position); return; }
        setErr("This order isn't part of any trade Stonky has a record of.");
      })
      .catch((e) => { if (live) setErr((e as Error).message); });
    return () => { live = false; };
  }, [exitOrderId]);

  const [share, setShare] = useState<{ url: string; id: string; view_count: number; active: boolean } | null>(null);
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);

  // Publishing puts real prices, size and P&L on the open internet, so it is
  // never automatic — it happens only when the reader clicks, and it's shown
  // back with a live view count and a one-click takedown so the exposure
  // stays visible rather than becoming something you forgot you did.
  async function publish() {
    if (!story) return;
    setSharing(true);
    try {
      const r = await api.post<{ url: string; id: string; view_count: number; active: boolean }>(
        "/api/performance/trade/story/share", { trade_key: exitOrderId });
      setShare(r);
      try { await navigator.clipboard.writeText(r.url); setCopied(true); setTimeout(() => setCopied(false), 2500); }
      catch { /* clipboard blocked — the URL is shown below regardless */ }
    } catch { /* button stays available to retry */ }
    finally { setSharing(false); }
  }

  async function unpublish() {
    if (!share) return;
    setSharing(true);
    try { await api.del(`/api/performance/trade/story/share/${share.id}`); setShare(null); }
    catch { /* leave state as-is */ }
    finally { setSharing(false); }
  }

  // Name the printout after the trade, not the app. document.title is what
  // the browser offers as the PDF filename, so it's swapped for the duration
  // of the print and restored on afterprint (which fires whether the user
  // saves or cancels).
  function printStory() {
    const name = story
      ? storyFilename(story.identity)
      : openPosition
        ? `stonky_${openPosition.symbol.replace(/[/_]/g, "-").toUpperCase()}_${openPosition.side}_open-position`
        : null;
    if (!name) { window.print(); return; }
    const prev = document.title;
    document.title = name;
    const restore = () => {
      document.title = prev;
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    window.print();
    // Safety net: some browsers don't fire afterprint reliably.
    setTimeout(restore, 60000);
  }

  return (
    <div className="fixed inset-0 z-50 bg-ink overflow-y-auto" id="trade-story-print">
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #trade-story-print, #trade-story-print * { visibility: visible; }
          #trade-story-print { position: absolute; inset: 0; overflow: visible; }
          .no-print { display: none !important; }
        }
      `}</style>
      <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-4 py-3 border-b border-edge bg-ink/95 backdrop-blur no-print">
        <span className="font-black tracking-tight uppercase text-sm">Trade Story</span>
        <div className="flex gap-2">
          {story && story.graph_ids.length > 0 && (
            <button className="btn text-xs" onClick={() => { focusGraph(story.graph_ids); onClose(); }}>
              View full graph
            </button>
          )}
          {story && (
            share
              ? <button className="btn text-xs" onClick={unpublish} disabled={sharing}
                        title={`Public link · ${share.view_count} view(s)`}>
                  {sharing ? "…" : `Unpublish (${share.view_count} views)`}
                </button>
              : <button className="btn text-xs" onClick={publish} disabled={sharing}>
                  {sharing ? "Publishing…" : "Share publicly"}
                </button>
          )}
          <button className="btn text-xs" onClick={printStory}>Print / Share</button>
          <button className="btn text-xs" onClick={onClose}>✕ Close</button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 md:p-8 space-y-6">
        {err && <div className="text-down text-sm">{err}</div>}
        {openPosition && <OpenPositionCard p={openPosition} />}
        {!story && !err && !openPosition && <div className="text-slate-500 text-sm font-sans">loading…</div>}
        {share && (
          <div className="card border-gold/50 space-y-1 no-print">
            <div className="label mb-0 text-gold">Published to a public link</div>
            <div className="text-xs text-slate-400 font-sans">
              Anyone with this URL can read this one trade — its prices, size and P&amp;L. It is
              live until you unpublish it. {share.view_count} view{share.view_count === 1 ? "" : "s"} so far.
              {copied && <span className="text-accent"> · copied to clipboard</span>}
            </div>
            <div className="font-mono text-xs break-all text-accent">{share.url}</div>
          </div>
        )}
        {story && <StoryBody story={story} />}
      </div>
    </div>
  );
}

export function StoryBody({ story }: { story: Story }) {
  const { identity, backward, forward, summary, branching } = story;
  const scratch = identity.result === "scratch";
  const isReal = identity.money === "real";
  return (
    <>
      {/* Money banner — first thing on the page, on purpose. "hard to tell
          whether commentary is about the live or the paper book" (Alan,
          2026-08-06): a small badge among several others was too easy to
          miss, so real vs. simulated capital now gets its own full-width
          strip before anything else renders. */}
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider font-sans ${
        isReal ? "bg-down/20 text-down border border-down/40" : "bg-slate-700/30 text-slate-300 border border-slate-600/40"}`}>
        <span className={`inline-block w-2 h-2 rounded-full ${isReal ? "bg-down" : "bg-slate-400"}`} />
        {isReal ? "Live · Real money" : "Paper · Simulation, no real capital"}
        <span className="font-normal normal-case text-slate-400 ml-1">— {BOOK_LABEL[identity.book] ?? identity.book}</span>
      </div>

      {/* Header */}
      <div className="space-y-1">
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="text-2xl font-black">{identity.symbol}</span>
          <span className={`text-sm font-sans font-semibold ${identity.side === "short" ? "text-down" : "text-up"}`}>
            {identity.side.toUpperCase()}
          </span>
          <span className="text-sm text-slate-400 font-sans">{identity.qty} {unitWord(identity.symbol)} · {identity.junior ?? "—"}</span>
          <span className={`ml-auto text-xl font-bold font-mono ${scratch ? "text-slate-500" : identity.pnl >= 0 ? "text-up" : "text-down"}`}>
            {scratch ? "scratch" : usd(identity.pnl)}{" "}
            <span className="text-sm font-normal">({identity.pnl_pct >= 0 ? "+" : ""}{identity.pnl_pct}%)</span>
          </span>
        </div>
        <div className="text-sm text-slate-400 font-mono">
          {px(identity.symbol, identity.entry_price)} → {px(identity.symbol, identity.exit_price)}
          {identity.entry_time && ` · entered ${fmtTime(identity.entry_time)}`}
          {identity.exit_time && ` · exited ${fmtTime(identity.exit_time)}`}
          {identity.hold_min != null && ` · held ${fmtHold(identity.hold_min)}`}
        </div>
      </div>

      {/* Executive summary — the conclusion, up front. Deterministic: every
          line restates figures shown further down, so it can never disagree
          with its own evidence. */}
      {summary && (
        <section className="card border-accent/40 space-y-2">
          <h2 className="label text-accent mb-0">Executive summary</h2>
          <p className="text-base font-semibold font-sans text-slate-100">{summary.headline}</p>
          <p className="text-xs font-sans italic text-slate-500">{summary.purpose_note}</p>
          {summary.root_cause && (
            <div className="border border-down/40 bg-down/10 rounded-md px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider font-bold text-down mb-0.5">Root cause — real money</div>
              <div className="text-sm font-sans text-slate-200">{summary.root_cause}</div>
            </div>
          )}
          {summary.points.length > 0 && (
            <ul className="space-y-1 text-sm font-sans text-slate-300 list-disc pl-5">
              {summary.points.map((pt, i) => <li key={i}>{pt}</li>)}
            </ul>
          )}
          {summary.flags.length > 0 && (
            <div className="pt-1 space-y-1">
              {summary.flags.map((f, i) => (
                <div key={i} className="text-sm font-sans text-gold">⚠ {f}</div>
              ))}
            </div>
          )}
        </section>
      )}

      <BranchingSection branching={branching} />

      <StoryNetwork network={story.network} />

      {/* Backward: what caused it */}
      <section className="space-y-3">
        <h2 className="label text-accent">← What led to this trade</h2>
        {!backward.proposal && !backward.mechanical && (
          <div className="text-sm text-slate-600 font-sans">no proposal on record for this trade&rsquo;s entry.</div>
        )}
        {!backward.proposal && backward.mechanical && (
          <MechanicalCard mechanical={backward.mechanical} />
        )}
        {backward.board_session && (
          <BoardSessionCard session={backward.board_session} />
        )}
        {backward.directives.length > 0 && (
          <div className="border border-edge rounded-lg p-3">
            <div className="label">Cited directive{backward.directives.length > 1 ? "s" : ""}</div>
            <ol className="space-y-1.5 mt-1">
              {backward.directives.map((d, i) => (
                <li key={i} className="text-sm flex gap-2 items-start">
                  <span className="text-accent font-mono shrink-0">{d.seq}.</span>
                  {d.found ? (
                    <span>
                      {d.text}
                      {d.stale && (
                        <span className="ml-2 badge bg-down/20 text-down align-middle">
                          STALE — from {d.trading_day}, not this trade&rsquo;s day
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="text-slate-600 italic">directive #{d.seq} not found on record</span>
                  )}
                </li>
              ))}
            </ol>
          </div>
        )}
        {backward.proposal && (
          <div className="border border-edge rounded-lg p-3 space-y-2">
            <div className="label">Proposal rationale</div>
            <p className="text-sm text-slate-200 font-sans">{backward.proposal.rationale}</p>
            <div className="text-xs text-slate-500 font-mono">
              {backward.proposal.entry_type}
              {backward.proposal.limit_price != null && ` @ ${backward.proposal.limit_price}`}
              {backward.proposal.exit_target != null && ` · target ${backward.proposal.exit_target}`}
              {backward.proposal.exit_stop != null && ` · stop ${backward.proposal.exit_stop}`}
              {backward.proposal.exit_time && ` · time-exit ${backward.proposal.exit_time}`}
            </div>
            {backward.proposal.risk_report && (
              <div className="text-xs">
                <span className={backward.proposal.risk_report.allowed ? "text-up" : "text-down"}>
                  risk gate: {backward.proposal.risk_report.allowed ? "PASSED" : "BLOCKED"}
                </span>
                {backward.proposal.risk_report.checks && (
                  <ul className="mt-1 space-y-0.5 font-mono text-slate-500">
                    {backward.proposal.risk_report.checks.filter((c) => !c.ok).map((c, i) => (
                      <li key={i} className="text-down">✗ {c.name}: {c.detail}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
        {backward.senior_ruling && (
          <div className="border-l-2 border-gold pl-3 py-1">
            <div className="text-gold text-xs font-mono uppercase tracking-widest mb-1">
              Senior ruling — {backward.senior_ruling.decision}
            </div>
            <p className="text-sm text-slate-200 italic font-sans">&ldquo;{backward.senior_ruling.reasoning}&rdquo;</p>
          </div>
        )}
      </section>

      {/* Forward: what happened after */}
      <section className="space-y-3">
        <h2 className="label text-accent">What happened after →</h2>
        <div className="border border-edge rounded-lg p-3">
          <PricePath path={forward.market_forensics.path} entryPx={identity.entry_price} exitPx={identity.exit_price}
                     entryTime={identity.entry_time} exitTime={identity.exit_time} />
        </div>
        {/* The exit ladder. The backend has always sent if_held/best_exit and
            this view has always declared their types — it just never drew
            them, so the chart said "the price kept going" without ever saying
            what that was WORTH. Performance's what-if panel had it; the full
            story, which is the more considered read, did not. */}
        {(forward.market_forensics.if_held.length > 0 || forward.market_forensics.best_exit) && (
          <div className="grid md:grid-cols-2 gap-5 text-sm border border-edge rounded-lg p-3">
            <div>
              <div className="label">What if we&rsquo;d held past the exit?</div>
              {forward.market_forensics.if_held.length === 0 && (
                <div className="text-slate-600 text-xs font-sans">no post-exit data</div>
              )}
              <table className="w-full font-mono text-xs">
                <tbody>
                  {forward.market_forensics.if_held.map((h, i) => (
                    <tr key={i} className="border-t border-edge">
                      <td className="py-1 text-slate-500 w-20">{h.label}</td>
                      <td className="text-slate-400">{px(identity.symbol, h.price)}</td>
                      <td className={`text-right ${h.vs_actual >= 0 ? "text-up" : "text-down"}`}>
                        {h.vs_actual >= 0 ? "+" : ""}{usd(h.vs_actual)}{" "}
                        <span className="text-slate-600 font-sans">vs our exit</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {forward.market_forensics.best_exit && (
              <div>
                <div className="label">Best exit available</div>
                <div className="font-mono text-slate-300">
                  {px(identity.symbol, forward.market_forensics.best_exit.price)}
                  {" at "}{fmtTime(forward.market_forensics.best_exit.t)}
                </div>
                <div className={`text-xs font-sans mt-0.5 ${
                  forward.market_forensics.best_exit.left_on_table > 0.005 ? "text-gold" : "text-slate-500"}`}>
                  {forward.market_forensics.best_exit.left_on_table > 0.005
                    ? `left ${usd(forward.market_forensics.best_exit.left_on_table)} on the table`
                    : "we exited at/near the peak"}
                </div>
              </div>
            )}
          </div>
        )}
        {Object.keys(forward.market_forensics.signal_freshness).length > 0 && (
          <div className="text-sm font-sans text-slate-400">
            {Object.entries(forward.market_forensics.signal_freshness).map(([k, v]) => (
              <div key={k}>
                {k} signal was <span className={v.min_before_entry > 45 ? "text-gold" : "text-up"}>{v.min_before_entry}m</span> old at entry
              </div>
            ))}
          </div>
        )}
        {forward.post_mortem && (
          <div className="border border-edge rounded-lg p-3">
            <div className="label">
              Post-mortem — {forward.post_mortem.trading_day}
              {!forward.post_mortem.mentions_symbol && (
                <span className="ml-2 text-slate-600 normal-case font-sans">(day-level review; doesn&rsquo;t specifically name {identity.symbol})</span>
              )}
            </div>
            <p className="text-sm text-slate-300 whitespace-pre-wrap font-sans mt-1 max-h-64 overflow-y-auto">
              {forward.post_mortem.content}
            </p>
          </div>
        )}
        {forward.trend_insights.map((t, i) => (
          <div key={i} className="border border-edge rounded-lg p-3">
            <div className="label">{t.kind === "lessons_audit" ? "Lessons audit" : "Trend digest"} — {new Date(t.created_at).toLocaleDateString()}</div>
            <p className="text-sm text-slate-300 whitespace-pre-wrap font-sans mt-1">{t.content}</p>
          </div>
        ))}
      </section>
    </>
  );
}

function OpenPositionCard({ p }: { p: OpenPosition }) {
  return (
    <div className="border border-edge rounded-lg p-4 space-y-2">
      <div className="text-lg font-bold">{p.symbol} <span className="text-sm font-normal text-slate-400">{p.side} · {p.qty} · {p.book}</span></div>
      <p className="text-sm text-slate-300 font-sans">
        This position is still open — entered {fmtTime(p.entry_time)} at ${p.entry_px}, no exit yet.
        There&rsquo;s no full story to tell until it closes; check back after it exits.
      </p>
    </div>
  );
}

// Where the causal chain FORKS. A story is drawn as a line, but the graph
// behind it is a DAG: one exit closes several entry lots, one directive is
// cited by many proposals, one board session drives many trades. Reading a
// single leg as the whole story is how a good decision gets blamed for one
// bad outcome — so the decision's FULL consequence set is shown next to it
// (Alan, 2026-08-04: "what happens when the graph splits... we need to
// account for that complexity when tracing decisions").
function BranchingSection({ branching }: { branching: Branching }) {
  const fan = branching?.fan_in_same_exit;
  const split = branching?.split_exits_same_entry;
  const dfan = branching?.directive_fanout ?? [];
  const sess = branching?.session_fanout;
  const has = (fan?.count ?? 0) > 0 || (split?.count ?? 0) > 0 || dfan.length > 0 || !!sess;
  if (!has) return null;

  const money = signedUsd;
  const Row = ({ t }: { t: TradeBrief }) => (
    <li className="flex items-baseline gap-2 font-mono text-xs">
      <span className="text-slate-400">{t.qty} {t.symbol}</span>
      <span className="text-slate-600">{t.side}</span>
      <span className={`ml-auto ${t.pnl >= 0 ? "text-up" : "text-down"}`}>{money(t.pnl)}</span>
    </li>
  );

  return (
    <section className="card border-edge space-y-4">
      <div>
        <h2 className="label mb-0">Where this splits</h2>
        <p className="text-xs text-slate-500 font-sans">
          This trade is one branch of a larger decision — these are the others it shares a cause with.
        </p>
      </div>

      {(fan?.count ?? 0) > 0 && (
        <div>
          <div className="text-xs font-sans text-slate-400 mb-1">
            One exit order closed <span className="text-slate-200">{(fan!.count) + 1} lots</span>
            {fan!.combined && (
              <> — together{" "}
                <span className={fan!.combined.net_pnl >= 0 ? "text-up" : "text-down"}>
                  {money(fan!.combined.net_pnl)}
                </span>{" "}
                ({fan!.combined.wins}W/{fan!.combined.losses}L)
              </>
            )}
          </div>
          <ul className="space-y-0.5">{fan!.siblings.map((t, i) => <Row key={i} t={t} />)}</ul>
        </div>
      )}

      {(split?.count ?? 0) > 0 && (
        <div>
          <div className="text-xs font-sans text-slate-400 mb-1">
            This entry was closed across <span className="text-slate-200">{split!.count + 1} separate exits</span>
          </div>
          <ul className="space-y-0.5">{split!.siblings.map((t, i) => <Row key={i} t={t} />)}</ul>
        </div>
      )}

      {dfan.map((d) => (
        <div key={d.seq}>
          <div className="text-xs font-sans text-slate-400 mb-1">
            Directive <span className="text-accent">#{d.seq}</span> also drove{" "}
            <span className="text-slate-200">{d.trades} other trade{d.trades > 1 ? "s" : ""}</span> —{" "}
            <span className={d.net_pnl >= 0 ? "text-up" : "text-down"}>{money(d.net_pnl)}</span>{" "}
            ({d.wins}W/{d.losses}L)
          </div>
          <ul className="space-y-0.5">{d.peers.map((t, i) => <Row key={i} t={t} />)}</ul>
        </div>
      ))}

      {sess && (
        <div className="border-t border-edge pt-3">
          <div className="text-sm font-sans text-slate-300">
            The board session behind this trade issued directives #{sess.directives.join(", ")} and drove{" "}
            <span className="text-slate-100 font-semibold">{sess.trades} trades</span> netting{" "}
            <span className={sess.net_pnl >= 0 ? "text-up" : "text-down"}>{money(sess.net_pnl)}</span>{" "}
            ({sess.wins}W/{sess.losses}L).
          </div>
          <div className="text-xs text-slate-500 font-sans mt-1">
            This trade contributed {money(sess.this_trade_pnl)} of that. Judge the decision on the whole set.
          </div>
        </div>
      )}
    </section>
  );
}

// The causal chain, drawn in the story itself. Previously the only way to see
// it was the "View full graph" button — which jumps to Graph Explorer, and a
// PUBLIC reader has no Graph Explorer to jump to, so on a shared story the
// chain was simply unreachable (2026-08-04). Node types are laid out by causal
// depth so it reads left-to-right as the decision actually happened.
const STORY_LAYERS: { title: string; types: string[] }[] = [
  { title: "What we knew", types: ["MacroBrief", "SectorDigest", "TriggerEvent"] },
  { title: "Who decided", types: ["BoardSession", "Perspective"] },
  { title: "What was ordered", types: ["Directive", "SeniorRuling", "Constitution"] },
  { title: "What was proposed", types: ["Proposal"] },
  { title: "Market", types: ["Order", "Fill"] },
  { title: "What we concluded", types: ["PostMortem", "TrendInsight"] },
];

function StoryNetwork({ network }: { network?: Story["network"] }) {
  const nodes = network?.nodes ?? [];
  if (nodes.length === 0) return null;
  const edges = network?.edges ?? [];

  // Cap each column. Bridging (graph.py) reconnects otherwise-adrift nodes,
  // which for a trade story can drag in a dozen post-mortems from OTHER days
  // that the trend digests synthesize — correct, but it turns one column into
  // a 20-node stack and squashes the actual decision path into the left
  // margin. Show the first few and say how many were held back, rather than
  // silently hiding them or letting one column swamp the diagram.
  const PER_COL = 6;
  const known = new Set(STORY_LAYERS.flatMap((l) => l.types));
  const cols = STORY_LAYERS
    .map((l) => {
      const all = nodes.filter((n) => l.types.includes(n.type));
      return { title: l.title, nodes: all.slice(0, PER_COL), hidden: Math.max(0, all.length - PER_COL) };
    })
    .filter((c) => c.nodes.length);
  const rest = nodes.filter((n) => !known.has(n.type));
  if (rest.length) cols.push({ title: "Also linked", nodes: rest.slice(0, PER_COL),
                               hidden: Math.max(0, rest.length - PER_COL) });

  const shown = new Set(cols.flatMap((c) => c.nodes.map((n) => n.id)));
  const W = 960, COL = W / Math.max(cols.length, 1), ROW = 48, PAD = 40;
  const H = PAD * 2 + ROW * Math.max(...cols.map((c) => c.nodes.length), 1);
  const pos = new Map<string, { x: number; y: number }>();
  cols.forEach((c, ci) =>
    c.nodes.forEach((n, ri) =>
      pos.set(n.id, { x: ci * COL + COL / 2, y: PAD + ri * ROW + ROW / 2 })));

  return (
    <section className="space-y-2">
      <h2 className="label text-accent">The causal network — {nodes.length} nodes · {edges.length} links</h2>
      <div className="border border-edge rounded-lg p-2 overflow-x-auto bg-slate-900/30">
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ minWidth: 620, display: "block" }}>
          {cols.map((c, ci) => (
            <g key={`h${ci}`}>
              <text x={ci * COL + COL / 2} y={14} textAnchor="middle"
                    className="fill-slate-600" style={{ fontSize: 9, letterSpacing: 1 }}>
                {c.title.toUpperCase()}
              </text>
              {c.hidden > 0 && (
                <text x={ci * COL + COL / 2} y={H - 8} textAnchor="middle"
                      className="fill-slate-600" style={{ fontSize: 8 }}>
                  +{c.hidden} more
                </text>
              )}
            </g>
          ))}
          {edges.map((e, i) => {
            const a = pos.get(e.src), b = pos.get(e.dst);
            if (!a || !b) return null;
            const mx = (a.x + b.x) / 2;
            return (
              <g key={`e${i}`}>
                <path d={`M ${a.x} ${a.y} C ${mx} ${a.y}, ${mx} ${b.y}, ${b.x} ${b.y}`}
                      fill="none" className="stroke-slate-700" strokeWidth={1} />
                <text x={mx} y={(a.y + b.y) / 2 - 3} textAnchor="middle"
                      className="fill-slate-700" style={{ fontSize: 7 }}>{e.rel}</text>
              </g>
            );
          })}
          {nodes.filter((n) => shown.has(n.id)).map((n) => {
            const pt = pos.get(n.id);
            if (!pt) return null;
            // Entry vs exit at a glance — two Orders in a round trip used to
            // render as identical dots both labeled with the bare symbol.
            const dotClass = n.type === "Order"
              ? (n.side === "buy" ? "fill-up" : n.side === "sell" ? "fill-down" : "fill-accent")
              : "fill-accent";
            return (
              <g key={n.id} style={{ cursor: n.hover ? "help" : undefined }}>
                {/* Native tooltip: what this step provided, what influenced
                    it, what it set up — computed server-side from the same
                    edges drawn here, so it can't disagree with the diagram. */}
                {n.hover && <title>{n.hover}</title>}
                <circle cx={pt.x} cy={pt.y} r={5} className={dotClass} />
                <text x={pt.x} y={pt.y - 10} textAnchor="middle"
                      className="fill-slate-300" style={{ fontSize: 8.5, fontWeight: 700 }}>{n.type}</text>
                <text x={pt.x} y={pt.y + 15} textAnchor="middle"
                      className={n.type === "Order" ? "fill-slate-300" : "fill-slate-500"}
                      style={{ fontSize: 8 }}>
                  {(n.label || n.type).slice(0, n.type === "Order" ? 34 : 22)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] uppercase tracking-wider text-slate-600">
        {Object.entries(network?.by_type ?? {}).map(([k, v]) => <span key={k}>{k} ×{v}</span>)}
      </div>
    </section>
  );
}

function MechanicalCard({ mechanical }: { mechanical: Mechanical }) {
  const m = mechanical;
  return (
    <div className="border border-edge rounded-lg p-3 space-y-2">
      <div className="label">Mechanical execution — no board directive, no LLM proposal</div>
      <p className="text-sm text-slate-200 font-sans">
        This is a crypto/forex sleeve trade — it runs from a standing weekly strike/watch rule, not
        a board-directed pick. The rule that fired: <span className="text-accent">{m.rule}</span>.
      </p>
      {m.rationale && <p className="text-sm text-slate-300 font-sans italic">&ldquo;{m.rationale}&rdquo;</p>}
      <div className="text-xs text-slate-500 font-mono space-x-3">
        {m.strike_price != null && <span>strike {m.strike_price}</span>}
        {m.trigger_price != null && <span>trigger {m.trigger_price}</span>}
        {m.target != null && <span>target {m.target}</span>}
        {m.invalidation_stop != null && <span>stop {m.invalidation_stop}</span>}
      </div>
      {m.budget_check && (
        <div className="text-xs text-slate-600 font-mono">
          budget: {Object.entries(m.budget_check).map(([k, v]) => `${k}=${v}`).join(" · ")}
        </div>
      )}
    </div>
  );
}

function BoardSessionCard({ session }: { session: BoardSession }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-edge rounded-lg p-3 space-y-2">
      <div className="flex items-baseline justify-between">
        <div className="label mb-0">
          Board session — {session.kind} · {session.started_at ? new Date(session.started_at).toLocaleString() : "—"}
        </div>
      </div>
      {session.summary && <p className="text-sm text-slate-300 font-sans">{session.summary}</p>}
      {session.stonky?.statement && (
        <div className="border-l-2 border-gold pl-3 py-1">
          <div className="text-gold text-xs font-mono uppercase tracking-widest mb-1">
            Stonky&rsquo;s ruling — {session.stonky.verdict ?? "RATIFY"}
          </div>
          <p className="text-sm text-slate-200 italic font-sans">&ldquo;{session.stonky.statement}&rdquo;</p>
        </div>
      )}
      {session.risk_officer?.notes && session.risk_officer.notes.length > 0 && (
        <div className="text-xs text-slate-400 font-sans">
          <span className="text-slate-500 uppercase tracking-wide">Risk Officer:</span>{" "}
          {session.risk_officer.notes.join(" ")}
        </div>
      )}
      {Object.keys(session.perspectives || {}).length > 0 && (
        <button className="text-xs text-accent no-print" onClick={() => setOpen((o) => !o)}>
          {open ? "▾ hide" : "▸ show"} member perspectives ({Object.keys(session.perspectives).length})
        </button>
      )}
      {open && (
        <div className="space-y-2">
          {Object.entries(session.perspectives).map(([key, p]) => (
            <div key={key} className="border-t border-edge pt-2">
              <div className="text-xs font-semibold text-slate-300 flex justify-between">
                <span>{key.replace("board.", "")}</span>
                <span className="text-slate-600 font-mono">{p.model}</span>
              </div>
              <p className="text-xs text-slate-400 whitespace-pre-wrap font-sans mt-1">{p.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
